import React, { useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { getResultActionOptions } from "../../../utils/data";

import Button from "../../themed/button";
import Conditional from "../../common/conditional";
import Input from "../../themed/input/input";
import Select from "../../themed/select/select";

const HardnessItemReadingSchema = z.object({
    key: z.string().min(1, 'Reading key is required'),
    value: z.coerce.number(),
});

const HardnessItemSchema = z.object({
    id: z.number().positive().optional(),
    item: z.string().min(1, 'Item Name is required'),
    location: z.string().min(1, "Test Location is required"),
    result: z.string().min(1, "Result is required"),
    readings: z.array(HardnessItemReadingSchema),
});

type Props = {
    onSave: (item: HardnessItem) => void;
    onCancel?: () => void;
    initialItem?: HardnessItem;
}

export type HardnessItem = z.infer<typeof HardnessItemSchema>;
export type HardnessItemReading = z.infer<typeof HardnessItemReadingSchema>;

const HardnessResultSummaryForm: React.FC<Props> = ({ initialItem, onSave, onCancel }) => {
    const form = useForm<HardnessItem>({
        resolver: zodResolver(HardnessItemSchema),
        mode: 'all',
        defaultValues: { location: "", item: "", result: "", readings: [{ key: "", value: 0 }] },
    });

    const fields = useFieldArray({
        control: form.control,
        name: "readings",
    });

    const handleSaveHardnessItem = (data: HardnessItem) => {
       onSave(data);
       form.reset(); 
    };

    useEffect(() => {
        if (initialItem) {
            form.reset(initialItem);
        } else {
            form.reset({ location: "", item: "", result: "", readings: [{ key: "", value: 0 }] });
        }
    }, [initialItem, form]);

    return ( 
        <form onSubmit={form.handleSubmit(handleSaveHardnessItem)}>
            <div className="container-fluid">
                <div className="row">
                    <div className="col">
                        <Controller
                            control={form.control}
                            name='location'
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Location of Test"
                                    placeholder="Enter Location of Test"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.location ? form.formState.errors.location.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={form.control}
                            name='item'
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Item"
                                    placeholder="Enter Item"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.item ? form.formState.errors.item.message : ''}
                                />
                            )}
                        />
                    </div>

                    <div className="col">
                        <Controller
                            control={form.control}
                            name="result"
                            render={({ field }) => (
                                <Select
                                    label="Result"
                                    placeholder="Select Result"
                                    options={getResultActionOptions()}
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={form.formState.errors.result ? form.formState.errors.result.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div>
                    <div className="form-title mb-4">Readings</div>

                    {fields.fields.map((initialField, index) => (
                        <div key={initialField.id}>
                            <div className="row mt-4">
                                <div className="col">
                                    <Controller
                                        control={form.control}
                                        name={`readings.${index}.key`}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Key"
                                                value={field.value}
                                                onChange={field.onChange}
                                                name={field.name}
                                                error={form.formState.errors.readings?.[index]?.key ? form.formState.errors.readings?.[index]?.key?.message : ''}
                                            />
                                        )}
                                    />
                                </div>
                            
                                <div className="col">
                                    <Controller
                                        control={form.control}
                                        name={`readings.${index}.value`}
                                        render={({ field }) => (
                                            <Input
                                                type='number'
                                                placeholder="Key"
                                                value={field.value}
                                                onChange={field.onChange}
                                                name={field.name}
                                                error={form.formState.errors.readings?.[index]?.value ? form.formState.errors.readings?.[index]?.value?.message : ''}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <Button
                                onClick={() => fields.remove(index)} 
                                icon="trash"
                                type="button"
                                label="Remove" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    ))}

                    <div className="d-flex justify-content-end align-item-center">
                        <div className="mx-2">
                            <Button
                                type="button"
                                label="Add Reading"
                                icon="plus"
                                style={{ backgroundColor: '#004e2a' }}
                                onClick={() => fields.append({ key: "", value: 0 })}
                            />
                        </div>
                    </div>
                </div>

                <div className="divider my-4"></div>
                
                <div className="d-flex justify-content-end align-item-center">
                    <Conditional isVisible={!!onCancel}>
                        <div className="me-3">
                            <Button
                                onClick={onCancel} 
                                icon="times"
                                type="button"
                                label="Cancel" 
                                style={{ backgroundColor: 'red' }} 
                            />
                        </div>
                    </Conditional>
				    
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid}
                        label={form.formState.isSubmitting ? "Saving Information..." : "Save Information"}
                    />
                </div>
            </div>
        </form>
    );
};
 
export default HardnessResultSummaryForm;